import React from "react";
import {
    BAND_MULTIPLIER_DEFAULT,
    BAND_MULTIPLIER_MAX,
    BAND_MULTIPLIER_MIN,
    BAND_MULTIPLIER_STEP,
    applyBandMultiplier,
    clampBandMultiplier,
    getBandMultiplier,
    setBandMultiplier,
    subscribeBandMultiplier,
} from "../../utils/bollingerBandScale";

const formatNumberWithCommaAndDecimal = (number, decimalPlaces = 3) => {
    if (isNaN(number)) return "";
    return Number(number).toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });
};

const formatMultiplier = (m) => String(clampBandMultiplier(m));

// 눈금 라벨 1개의 실측 폭(px). "1,391.998" 9자 × JetBrains Mono 12px ≈ 65px + 여유 (DAS-175).
const LABEL_WIDTH_PX = 68;
// 라벨 사이 최소 간격(px). 이보다 좁아지면 붙어 보인다.
const LABEL_GAP_PX = 8;

class BollingerGauge extends React.Component {
    constructor(props) {
        super(props);
        const multiplier = getBandMultiplier();
        this.state = {
            multiplier,
            draft: formatMultiplier(multiplier),
            editing: false,
            // 눈금 라벨 행의 실측 폭. 0이면 아직 못 쟀다는 뜻이고, 그때는 겹칠 수 있는
            // Mid/M-High 눈금을 그리지 않는다 (값은 아래 요약 카드에 항상 남는다).
            trackWidth: 0,
        };
        this.unsubscribeMultiplier = null;
        this.resizeObserver = null;
        this.labelRowRef = React.createRef();
    }

    componentDidMount() {
        // 다른 게이지 인스턴스(다른 전략 섹션)나 다른 탭에서 바꾼 배율을 실시간 반영
        this.unsubscribeMultiplier = subscribeBandMultiplier((multiplier) => {
            this.setState((prev) => ({
                multiplier,
                draft: prev.editing ? prev.draft : formatMultiplier(multiplier),
            }));
        });
        // 화면 폭이 아니라 트랙 실측 폭으로 판단한다 — 사이드바 접기/펼치기, 태블릿, 분할 화면처럼
        // 뷰포트만으로는 알 수 없는 중간 폭에서도 겹침 판정이 맞아야 한다.
        this.measureTrack();
        if (typeof ResizeObserver !== "undefined" && this.labelRowRef.current) {
            this.resizeObserver = new ResizeObserver(this.measureTrack);
            this.resizeObserver.observe(this.labelRowRef.current);
        } else if (typeof window !== "undefined") {
            window.addEventListener("resize", this.measureTrack);
        }
    }

    componentWillUnmount() {
        if (this.unsubscribeMultiplier) this.unsubscribeMultiplier();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        } else if (typeof window !== "undefined") {
            window.removeEventListener("resize", this.measureTrack);
        }
    }

    measureTrack = () => {
        const el = this.labelRowRef.current;
        if (!el) return;
        const width = el.offsetWidth || 0;
        if (width !== this.state.trackWidth) this.setState({ trackWidth: width });
    };

    commitMultiplier = (value) => {
        const next = setBandMultiplier(value);
        this.setState({ multiplier: next, draft: formatMultiplier(next), editing: false });
    };

    stepMultiplier = (direction) => {
        // Safari/iOS는 버튼 클릭 시 input blur가 선행되지 않으므로,
        // 커밋 전 draft가 있으면 그 값을 기준으로 스텝한다 (입력값 소실 방지)
        const { multiplier, draft, editing } = this.state;
        const trimmed = draft.trim();
        const parsed = Number(trimmed);
        const base =
            editing && trimmed !== "" && Number.isFinite(parsed)
                ? clampBandMultiplier(parsed)
                : multiplier;
        this.commitMultiplier(base + direction * BAND_MULTIPLIER_STEP);
    };

    onDraftChange = (event) => {
        this.setState({ draft: event.target.value, editing: true });
    };

    onDraftBlur = () => {
        // 빈 문자열은 Number('')===0으로 finite 판정되므로 명시적으로 이전 값 복원
        const trimmed = this.state.draft.trim();
        const parsed = Number(trimmed);
        if (trimmed !== "" && Number.isFinite(parsed)) {
            this.commitMultiplier(parsed);
        } else {
            this.setState((prev) => ({ draft: formatMultiplier(prev.multiplier), editing: false }));
        }
    };

    onDraftKeyDown = (event) => {
        if (event.key === "Enter") {
            event.currentTarget.blur();
        } else if (event.key === "Escape") {
            this.setState((prev) => ({ draft: formatMultiplier(prev.multiplier), editing: false }));
        }
    };

    renderMultiplierControl() {
        const { multiplier, draft } = this.state;
        // 모바일 터치 타겟 44px(h-11/w-11) 충족, 데스크톱은 컴팩트(h-6/w-6)
        const stepButtonClass =
            "flex h-11 w-11 items-center justify-center rounded-md text-sm font-semibold text-content-secondary transition-colors hover:bg-surface-hover hover:text-content disabled:cursor-not-allowed disabled:opacity-40 sm:h-6 sm:w-6";

        return (
            <div className="flex items-center gap-0.5 rounded-lg border border-surface-border bg-surface-elevated px-1.5 py-0.5">
                {/* 배율 = 구동 전략 safer.json 의 Level 과 동일 의미 (DAS-162) */}
                <span className="pr-1 text-xs font-medium text-content-muted">Level ×</span>
                <button
                    type="button"
                    aria-label={`밴드 배율 ${BAND_MULTIPLIER_STEP} 감소`}
                    disabled={multiplier <= BAND_MULTIPLIER_MIN}
                    onClick={() => this.stepMultiplier(-1)}
                    className={stepButtonClass}
                >
                    −
                </button>
                <input
                    type="text"
                    inputMode="decimal"
                    value={draft}
                    onChange={this.onDraftChange}
                    onBlur={this.onDraftBlur}
                    onKeyDown={this.onDraftKeyDown}
                    aria-label={`볼린저 밴드 배율 (${BAND_MULTIPLIER_MIN}~${BAND_MULTIPLIER_MAX})`}
                    className="min-h-11 w-9 bg-transparent text-center font-num text-xs font-semibold text-content focus:outline-none sm:min-h-6"
                />
                <button
                    type="button"
                    aria-label={`밴드 배율 ${BAND_MULTIPLIER_STEP} 증가`}
                    disabled={multiplier >= BAND_MULTIPLIER_MAX}
                    onClick={() => this.stepMultiplier(1)}
                    className={stepButtonClass}
                >
                    +
                </button>
                {multiplier !== BAND_MULTIPLIER_DEFAULT && (
                    <button
                        type="button"
                        aria-label={`밴드 배율 초기화 (${BAND_MULTIPLIER_DEFAULT})`}
                        title={`초기화 (×${BAND_MULTIPLIER_DEFAULT})`}
                        onClick={() => this.commitMultiplier(BAND_MULTIPLIER_DEFAULT)}
                        className={stepButtonClass}
                    >
                        ↺
                    </button>
                )}
            </div>
        );
    }

    render() {
        const { low, middle, high, currentRatio, exchangeRate, stale, computedAtMs, exchangeRateSource } = this.props;
        const { multiplier } = this.state;

        const rawLow = Number(low);
        const rawMiddle = Number(middle);
        const rawHigh = Number(high);
        const currentRatioNum = Number(currentRatio);

        // 모두 유효하지 않으면 렌더링 안 함
        if (!Number.isFinite(rawLow) && !Number.isFinite(rawMiddle) && !Number.isFinite(rawHigh)) {
            return null;
        }

        // 배율 적용: middle 기준으로 상/하단 밴드 폭 확대/축소
        const adjusted = applyBandMultiplier({ low: rawLow, middle: rawMiddle, high: rawHigh }, multiplier);
        const lowNum = adjusted.low;
        const middleNum = adjusted.middle;
        const highNum = adjusted.high;

        // low > high이면 swap
        let effectiveLow = lowNum;
        let effectiveHigh = highNum;
        if (Number.isFinite(effectiveLow) && Number.isFinite(effectiveHigh) && effectiveLow > effectiveHigh) {
            [effectiveLow, effectiveHigh] = [effectiveHigh, effectiveLow];
        }

        // 마커 위치 계산 (0~100%) — rawPosition은 밴드 이탈 판정용으로 클램프 전 값 보존
        const showMarker = Number.isFinite(currentRatioNum);
        let markerPosition = 50;
        let rawPosition = 50;
        if (showMarker && Number.isFinite(effectiveLow) && Number.isFinite(effectiveHigh)) {
            if (effectiveLow >= effectiveHigh) {
                markerPosition = 50;
            } else {
                rawPosition = ((currentRatioNum - effectiveLow) / (effectiveHigh - effectiveLow)) * 100;
                markerPosition = Math.max(0, Math.min(100, rawPosition));
            }
        }

        // mid-high 캡 값 (DAS-173, Strategy ARBITRAGE-148): (원본 High + Mid) / 2.
        // ⚠️ Level 배율 '미적용 원본' 기준이 Strategy 시맨틱이므로 raw 값으로 계산한다 —
        // 배율을 바꿔도 값 자체는 변하지 않고, 확장된 밴드 축 위의 '위치'만 달라진다.
        const midHighNum =
            Number.isFinite(rawMiddle) && Number.isFinite(rawHigh)
                ? (rawHigh + rawMiddle) / 2
                : NaN;

        // Mid 기준선의 밴드 내 실제 위치 — HighCap/최소폭 보정으로 밴드가 비대칭이면 50%가 아니다
        let midPosition = 50;
        if (
            Number.isFinite(middleNum) &&
            Number.isFinite(effectiveLow) &&
            Number.isFinite(effectiveHigh) &&
            effectiveLow < effectiveHigh
        ) {
            midPosition = Math.max(
                0,
                Math.min(100, ((middleNum - effectiveLow) / (effectiveHigh - effectiveLow)) * 100)
            );
        }

        // mid-high 마커 위치: 확장 밴드 축에 투영. 배율이 0.5 미만이면 mid-high가 확장 High보다
        // 커져 밴드 밖으로 나가므로(BAND_MULTIPLIER_MIN=0.1) 그 경우 마커는 숨긴다 —
        // 요약 카드의 값 표시는 위치와 무관하게 유지된다. 존 판정에는 관여하지 않는다(표시 전용).
        let midHighPosition = NaN;
        if (
            Number.isFinite(midHighNum) &&
            Number.isFinite(effectiveLow) &&
            Number.isFinite(effectiveHigh) &&
            effectiveLow < effectiveHigh
        ) {
            midHighPosition = ((midHighNum - effectiveLow) / (effectiveHigh - effectiveLow)) * 100;
        }
        const showMidHighMarker =
            Number.isFinite(midHighPosition) && midHighPosition >= 0 && midHighPosition <= 100;

        // ── 눈금 라벨 겹침 판정 (DAS-175) ──
        // 종전 가드는 "8%p 간격"과 clamp(4rem, …)이었는데, 둘 다 트랙이 넓을 때만 성립한다.
        // 모바일(트랙 ≈326px)에서 8%p는 26px — 라벨 한 칸(68px)에도 못 미쳐 M-High와 High가 겹쳤다.
        // 이제 실측 폭 위에서 라벨 사각형이 실제로 부딪히는지로 판정한다.
        // 양끝 Low/High는 항상 그리고, 자리가 모자라면 Mid → M-High 순으로 남긴다
        // (Mid는 기준선이라 우선순위가 높다). 숨겨진 값은 아래 요약 카드에서 읽는다.
        const { trackWidth } = this.state;
        const halfLabel = LABEL_WIDTH_PX / 2;
        const canPlaceLabels = trackWidth > LABEL_WIDTH_PX * 2 + LABEL_GAP_PX * 2;
        const labelCenterPx = (position) =>
            Math.min(trackWidth - halfLabel, Math.max(halfLabel, (trackWidth * position) / 100));
        const midCenterPx = labelCenterPx(midPosition);
        const midHighCenterPx = labelCenterPx(midHighPosition);
        const leftBoundary = LABEL_WIDTH_PX + LABEL_GAP_PX;
        const rightBoundary = trackWidth - LABEL_WIDTH_PX - LABEL_GAP_PX;
        const fitsBetweenEnds = (centerPx) =>
            centerPx - halfLabel >= leftBoundary && centerPx + halfLabel <= rightBoundary;

        const showMidLabel = canPlaceLabels && fitsBetweenEnds(midCenterPx);
        const showMidHighLabel =
            canPlaceLabels &&
            showMidHighMarker &&
            fitsBetweenEnds(midHighCenterPx) &&
            (!showMidLabel || midHighCenterPx - midCenterPx >= LABEL_WIDTH_PX + LABEL_GAP_PX);

        // 존 판정: Mid를 50으로 두는 정규화 좌표에서 판정해야 비대칭 밴드에서도
        // "Mid 아래=하단권/중립" 의미가 유지된다 (기하 위치 기준이면 Mid 아래인데 상단권으로 표시됨)
        const normalizedPosition =
            markerPosition <= midPosition
                ? midPosition > 0
                    ? (markerPosition / midPosition) * 50
                    : 0
                : midPosition < 100
                    ? 50 + ((markerPosition - midPosition) / (100 - midPosition)) * 50
                    : 100;
        const isOutOfBand = showMarker && (rawPosition < 0 || rawPosition > 100);
        let zone = null;
        if (showMarker) {
            if (rawPosition < 0) {
                zone = { label: "Low 이탈", badgeClass: "bg-gain-subtle text-gain" };
            } else if (rawPosition > 100) {
                zone = { label: "High 이탈", badgeClass: "bg-loss-subtle text-loss" };
            } else if (normalizedPosition < 35) {
                zone = { label: "하단권", badgeClass: "bg-gain-subtle text-gain" };
            } else if (normalizedPosition <= 65) {
                zone = { label: "중립", badgeClass: "bg-warning-subtle text-warning" };
            } else {
                zone = { label: "상단권", badgeClass: "bg-loss-subtle text-loss" };
            }
        }

        const exchangeRateNum = Number(exchangeRate);
        const isScaled = multiplier !== BAND_MULTIPLIER_DEFAULT;

        // 밴드 캐시 stale 표시 (DAS-181) — 계산이 일시 실패해 직전 값을 유지 중이면 서버가 stale=1을 내려준다.
        const isStale = Number(stale) === 1;
        const computedAtMsNum = Number(computedAtMs);
        const staleMinutesAgo = Number.isFinite(computedAtMsNum)
            ? Math.max(0, Math.round((Date.now() - computedAtMsNum) / 60000))
            : null;
        const staleLabel = staleMinutesAgo != null ? `이전 값 (${staleMinutesAgo}분 전)` : "이전 값";

        // 환율 라벨 — upbit_usdt 폴백(koreaexim TLS 실패 등) 시 김프 포함 시세임을 명시 (DAS-181)
        const exchangeRateLabel = exchangeRateSource === "upbit_usdt" ? "환율 (USDT 시세)" : "환율";
        // 배율 적용 시 hover로 raw(±2σ) 값 확인 가능
        const rawTitle = (rawValue) =>
            isScaled && Number.isFinite(rawValue)
                ? `raw ${formatNumberWithCommaAndDecimal(rawValue, 3)}`
                : undefined;

        // 눈금 라벨과 요약 카드가 같은 값 소스를 공유 (포맷/색상 드리프트 방지)
        const fmt3 = (n) => (Number.isFinite(n) ? formatNumberWithCommaAndDecimal(n, 3) : "N/A");
        const bandItems = [
            { label: "Low",  value: fmt3(lowNum),    colorClass: "text-gain",    title: rawTitle(rawLow) },
            { label: "Mid",  value: fmt3(middleNum), colorClass: "text-warning" },
            { label: "High", value: fmt3(highNum),   colorClass: "text-loss",    title: rawTitle(rawHigh) },
        ];
        // mid-high 요약 항목 — 배율 미적용 원본 기준값이라 rawTitle 대신 정의를 툴팁으로 고정
        const midHighItem = Number.isFinite(midHighNum)
            ? {
                  label: "M-High",
                  value: fmt3(midHighNum),
                  colorClass: "text-loss-light",
                  title: "mid-high = (원본 High + Mid) / 2 — Level 배율 미적용 (ARBITRAGE-148)",
              }
            : null;
        const summaryItems = [
            { label: exchangeRateLabel, value: Number.isFinite(exchangeRateNum) ? `₩${formatNumberWithCommaAndDecimal(exchangeRateNum, 2)}` : "-", colorClass: "text-content" },
            ...bandItems.slice(0, 2),
            ...(midHighItem ? [midHighItem] : []),
            bandItems[2],
        ];

        return (
            <div className="rounded-xl border border-surface-border bg-surface-card p-4 sm:p-5 animate-fade-in">
                {/* 헤더: 제목 + 밴드 배율 컨트롤 */}
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-content">현재 Ratio 위치</span>
                        {zone && (
                            <span
                                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${zone.badgeClass} ${isOutOfBand ? "animate-pulse" : ""}`}
                            >
                                {zone.label} · {Math.round(markerPosition)}%
                            </span>
                        )}
                        {isScaled && (
                            <span className="rounded-md bg-brand-subtle px-1.5 py-0.5 font-num text-xs font-semibold text-brand-light">
                                ×{formatMultiplier(multiplier)}
                            </span>
                        )}
                        {isStale && (
                            <span
                                role="status"
                                aria-label={staleLabel}
                                title="밴드 계산이 일시적으로 실패해 직전 값을 표시하고 있습니다"
                                className="rounded-md bg-surface-elevated px-1.5 py-0.5 text-xs font-medium text-content-muted"
                            >
                                {staleLabel}
                            </span>
                        )}
                    </div>
                    {this.renderMultiplierControl()}
                </div>

                {/* 게이지 바: 존 음영 트랙 + Mid 기준선 + 현재값 니들/칩 */}
                <div className="pt-9">
                    <div className="relative" style={showMidHighMarker ? { "--bb-midhigh-pos": `${midHighPosition}%` } : undefined}>
                        {showMarker && (
                            <div
                                className="bb-chip font-num text-xs font-semibold"
                                style={{ left: `clamp(3rem, ${markerPosition}%, calc(100% - 3rem))` }}
                                aria-hidden="true"
                            >
                                {fmt3(currentRatioNum)}
                            </div>
                        )}
                        {/* mid-high 캡 플래그 — 트랙 밖(상단)이라 track 형제로 렌더 (DAS-173) */}
                        {showMidHighMarker && <div className="bb-cap-flag" aria-hidden="true" />}
                        <div
                            className="bb-track"
                            style={{ "--bb-mid-pos": `${midPosition}%` }}
                            role="img"
                            aria-label={
                                zone
                                    ? `현재 ratio ${fmt3(currentRatioNum)} — 밴드 내 위치 ${Math.round(markerPosition)}% (${zone.label})`
                                    : "현재 ratio 데이터 없음"
                            }
                        >
                            <div className="bb-mid" aria-hidden="true" />
                            {showMidHighMarker && <div className="bb-cap-notch" aria-hidden="true" />}
                        </div>
                        {showMarker && (
                            <div
                                className={`bb-needle ${isOutOfBand ? "animate-pulse" : ""}`}
                                style={{ left: `${markerPosition}%` }}
                                aria-hidden="true"
                            />
                        )}
                    </div>

                    {/* 눈금 라벨 — 양끝(Low/High)은 항상, Mid·M-High는 실측 폭에서 겹치지 않을 때만 (DAS-175) */}
                    <div
                        ref={this.labelRowRef}
                        className="relative mt-1.5 flex items-center justify-between font-num text-xs"
                    >
                        {bandItems
                            .filter(({ label }) => label !== "Mid")
                            .map(({ label, value, colorClass, title }) => (
                                <span key={label} className={colorClass} title={title}>
                                    {value}
                                </span>
                            ))}
                        {showMidLabel && (
                            <span
                                className="absolute -translate-x-1/2 text-warning"
                                style={{ left: `${midCenterPx}px` }}
                            >
                                {fmt3(middleNum)}
                            </span>
                        )}
                        {showMidHighLabel && midHighItem && (
                            <span
                                className="absolute -translate-x-1/2 text-loss-light"
                                style={{ left: `${midHighCenterPx}px` }}
                                title={midHighItem.title}
                            >
                                {midHighItem.value}
                            </span>
                        )}
                    </div>
                </div>

                {/* 수치 요약 카드 — 모바일에서 눈금이 접히면 여기가 값을 읽는 유일한 곳이라
                    5항목(M-High 포함)일 때는 2열이 아니라 3열로 편다 (DAS-175) */}
                <div
                    className={`mt-3 grid gap-1.5 sm:gap-2 ${
                        midHighItem ? "grid-cols-3 sm:grid-cols-5" : "grid-cols-2 sm:grid-cols-4"
                    }`}
                >
                    {summaryItems.map(({ label, value, colorClass, title }) => (
                        <div key={label} title={title} className="rounded-lg bg-surface-elevated p-2 text-center">
                            <div className="text-xs font-semibold uppercase text-content-muted">{label}</div>
                            <div className={`mt-0.5 font-num text-xs font-semibold ${colorClass}`}>{value}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

export default BollingerGauge;
